'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie,  } from 'recharts';
import { Clock, CheckCircle2, PackageCheck, AlertCircle } from 'lucide-react';

// ── Mock data ──────────────────────────────────────────────────────────────────

const FULFILLMENT_TREND = [
  { month: 'Jan', rate: 78 },
  { month: 'Feb', rate: 82 },
  { month: 'Mar', rate: 75 },
  { month: 'Apr', rate: 88 },
  { month: 'May', rate: 91 },
  { month: 'Jun', rate: 86 },
  { month: 'Jul', rate: 93 },
];

const AVG_APPROVAL_TREND = [
  { month: 'Jan', hours: 52 },
  { month: 'Feb', hours: 47 },
  { month: 'Mar', hours: 61 },
  { month: 'Apr', hours: 38 },
  { month: 'May', hours: 34 },
  { month: 'Jun', hours: 29 },
  { month: 'Jul', hours: 26 },
];

const TOP_KIT_TYPES = [
  { name: 'Malaria Test Kit', requests: 312, fulfilled: 289 },
  { name: 'Blood Grouping Kit', requests: 278, fulfilled: 261 },
  { name: 'Dengue Test Kit', requests: 245, fulfilled: 218 },
  { name: 'HIV Test Kit', requests: 201, fulfilled: 195 },
  { name: 'Pregnancy Test Kit', requests: 189, fulfilled: 174 },
  { name: 'Typhoid Test Kit', requests: 167, fulfilled: 152 },
  { name: 'Hepatitis B Kit', requests: 143, fulfilled: 138 },
  { name: 'Glucose Test Kit', requests: 128, fulfilled: 121 },
];

const CUSTOMER_ORDER_TRENDS = [
  { month: 'Jan', fresh: 42, partial: 18, custom: 9 },
  { month: 'Feb', fresh: 38, partial: 22, custom: 11 },
  { month: 'Mar', fresh: 55, partial: 27, custom: 14 },
  { month: 'Apr', fresh: 61, partial: 31, custom: 17 },
  { month: 'May', fresh: 58, partial: 35, custom: 20 },
  { month: 'Jun', fresh: 72, partial: 40, custom: 23 },
  { month: 'Jul', fresh: 68, partial: 44, custom: 26 },
];

const ORDER_TYPE_DIST = [
  { name: 'Fresh', value: 394, color: '#0369a1' },
  { name: 'Partial', value: 217, color: '#0ea5e9' },
  { name: 'Custom', value: 120, color: '#7dd3fc' },
];

const TOP_CUSTOMERS = [
  { name: 'AIIMS PATNA', orders: 89, fulfilled: 82, pending: 7 },
  { name: 'SAFDARJUNG HOSPITAL', orders: 74, fulfilled: 68, pending: 6 },
  { name: 'RML HOSPITAL', orders: 61, fulfilled: 57, pending: 4 },
  { name: 'AIIMS DELHI', orders: 53, fulfilled: 49, pending: 4 },
  { name: 'PGIMER CHANDIGARH', orders: 47, fulfilled: 43, pending: 4 },
];

// ── Stat Card ──────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string;
  sub: string;
  icon: React.ReactNode;
  trend?: { dir: 'up' | 'down'; label: string };
  accent: string;
  accentBg: string;
}

function StatCard({ label, value, sub, icon, trend, accent, accentBg }: StatCardProps) {
  return (
    <div
      className="rounded-xl p-4 flex flex-col gap-3"
      style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
    >
      <div className="flex items-start justify-between gap-2">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: accentBg }}
        >
          <span style={{ color: accent }}>{icon}</span>
        </div>
        {trend && (
          <span
            className="text-[10px] font-600 px-2 py-0.5 rounded-full flex items-center gap-1"
            style={{
              background: trend.dir === 'up' ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)',
              color: trend.dir === 'up' ? '#16a34a' : '#dc2626',
            }}
          >
            {trend.dir === 'up' ? '↑' : '↓'} {trend.label}
          </span>
        )}
      </div>
      <div>
        <p className="text-2xl font-700 leading-none" style={{ color: '#0f2744' }}>{value}</p>
        <p className="text-xs mt-1 font-500" style={{ color: '#64748b' }}>{label}</p>
        <p className="text-[10px] mt-0.5" style={{ color: '#94a3b8' }}>{sub}</p>
      </div>
    </div>
  );
}

// ── Section Header ─────────────────────────────────────────────────────────────

function SectionHeader({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-sm font-700" style={{ color: '#0f2744' }}>{title}</h2>
      <p className="text-[11px] mt-0.5" style={{ color: '#94a3b8' }}>{sub}</p>
    </div>
  );
}

// ── Custom Tooltip ─────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 text-xs shadow-lg" style={{ background: '#0f2744', color: '#fff', border: 'none' }}>
      <p className="font-600 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color ?? '#7dd3fc' }}>
          {p.name}: <span className="font-600">{p.value}</span>
        </p>
      ))}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

type Range = '3M' | '6M' | '1Y';

export default function ReplenishmentMetricsPage() {
  const [range, setRange] = useState<Range>('6M');

  return (
    <AppLayout activePath="/admin/replenishment-metrics">
      <div className="px-4 lg:px-6 xl:px-8 py-5 max-w-screen-2xl mx-auto space-y-6">

        {/* Page header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-lg font-700" style={{ color: '#0f2744' }}>Replenishment Metrics</h1>
            <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
              Admin overview — fulfillment performance, approval efficiency, and order trends
            </p>
          </div>
          <div className="flex items-center gap-1.5 rounded-lg p-1" style={{ background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
            {(['3M', '6M', '1Y'] as Range[]).map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className="text-xs font-500 px-3 py-1.5 rounded-md transition-all"
                style={
                  range === r
                    ? { background: '#0369a1', color: '#fff', boxShadow: '0 1px 4px rgba(3,105,161,0.3)' }
                    : { color: '#64748b' }
                }
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Fulfillment Rate"
            value="93%"
            sub="Jul 2026 · target 90%"
            icon={<CheckCircle2 size={17} />}
            trend={{ dir: 'up', label: '+2.4%' }}
            accent="#16a34a"
            accentBg="rgba(22,163,74,0.1)"
          />
          <StatCard
            label="Avg Approval Time"
            value="26h"
            sub="Down from 52h in Jan"
            icon={<Clock size={17} />}
            trend={{ dir: 'up', label: '−50%' }}
            accent="#0369a1"
            accentBg="rgba(3,105,161,0.1)"
          />
          <StatCard
            label="Total Orders (YTD)"
            value="731"
            sub="Fresh 394 · Partial 217 · Custom 120"
            icon={<PackageCheck size={17} />}
            trend={{ dir: 'up', label: '+18%' }}
            accent="#7c3aed"
            accentBg="rgba(124,58,237,0.1)"
          />
          <StatCard
            label="Pending Approvals"
            value="25"
            sub="Across 5 facilities"
            icon={<AlertCircle size={17} />}
            trend={{ dir: 'down', label: '−8 vs last wk' }}
            accent="#b45309"
            accentBg="rgba(180,83,9,0.1)"
          />
        </div>

        {/* Row 1: Fulfillment rate + Avg approval time */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Fulfillment rate trend */}
          <div
            className="rounded-xl p-5"
            style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
          >
            <SectionHeader title="Fulfillment Rate Trend" sub="% of orders fulfilled on time per month" />
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={FULFILLMENT_TREND} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} domain={[60, 100]} unit="%" />
                <Tooltip content={<ChartTooltip />} />
                <Line
                  type="monotone"
                  dataKey="rate"
                  name="Fulfillment %"
                  stroke="#16a34a"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#16a34a', strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Avg approval time */}
          <div
            className="rounded-xl p-5"
            style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
          >
            <SectionHeader title="Avg Approval Time" sub="Average hours from request to approval decision" />
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={AVG_APPROVAL_TREND} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} unit="h" />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="hours" name="Avg Hours" radius={[4, 4, 0, 0]}>
                  {AVG_APPROVAL_TREND.map((entry, i) => (
                    <Cell
                      key={`cell-${i}`}
                      fill={entry.hours <= 30 ? '#0369a1' : entry.hours <= 45 ? '#0ea5e9' : '#7dd3fc'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Row 2: Top kit types */}
        <div
          className="rounded-xl p-5"
          style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
        >
          <SectionHeader title="Top-Requested Kit Types" sub="Total requests vs fulfilled — ranked by demand" />
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={TOP_KIT_TYPES}
              layout="vertical"
              margin={{ top: 0, right: 16, left: 8, bottom: 0 }}
              barCategoryGap="28%"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 10, fill: '#64748b' }}
                axisLine={false}
                tickLine={false}
                width={130}
              />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="requests" name="Requested" fill="#bfdbfe" radius={[0, 3, 3, 0]} />
              <Bar dataKey="fulfilled" name="Fulfilled" fill="#0369a1" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1.5 text-[11px]" style={{ color: '#64748b' }}>
              <span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#bfdbfe' }} />
              Requested
            </span>
            <span className="flex items-center gap-1.5 text-[11px]" style={{ color: '#64748b' }}>
              <span className="w-3 h-3 rounded-sm inline-block" style={{ background: '#0369a1' }} />
              Fulfilled
            </span>
          </div>
        </div>

        {/* Row 3: Customer order trends + order type distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* Customer order trends — spans 2 cols */}
          <div
            className="lg:col-span-2 rounded-xl p-5"
            style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
          >
            <SectionHeader title="Customer Order Trends" sub="Monthly order volume by replenishment type" />
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={CUSTOMER_ORDER_TRENDS} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="fresh" name="Fresh" stroke="#0369a1" strokeWidth={2} dot={{ r: 3, fill: '#0369a1', strokeWidth: 0 }} />
                <Line type="monotone" dataKey="partial" name="Partial" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 3, fill: '#0ea5e9', strokeWidth: 0 }} />
                <Line type="monotone" dataKey="custom" name="Custom" stroke="#7dd3fc" strokeWidth={2} dot={{ r: 3, fill: '#7dd3fc', strokeWidth: 0 }} />
              </LineChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-4 mt-2">
              {[{ label: 'Fresh', color: '#0369a1' }, { label: 'Partial', color: '#0ea5e9' }, { label: 'Custom', color: '#7dd3fc' }].map((l) => (
                <span key={l.label} className="flex items-center gap-1.5 text-[11px]" style={{ color: '#64748b' }}>
                  <span className="w-3 h-1.5 rounded-full inline-block" style={{ background: l.color }} />
                  {l.label}
                </span>
              ))}
            </div>
          </div>

          {/* Order type distribution */}
          <div
            className="rounded-xl p-5"
            style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
          >
            <SectionHeader title="Order Type Split" sub="YTD distribution" />
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={ORDER_TYPE_DIST}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={75}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {ORDER_TYPE_DIST.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string) => [`${value} orders`, name]}
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: 'none', background: '#0f2744', color: '#fff' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-1.5 mt-1">
              {ORDER_TYPE_DIST.map((d) => (
                <div key={d.name} className="flex items-center justify-between text-[11px]">
                  <span className="flex items-center gap-1.5" style={{ color: '#64748b' }}>
                    <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: d.color }} />
                    {d.name}
                  </span>
                  <span className="font-600" style={{ color: '#0f2744' }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Row 4: Top customers table */}
        <div
          className="rounded-xl p-5"
          style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
        >
          <SectionHeader title="Top Customers by Order Volume" sub="YTD orders, fulfillment, and pending breakdown" />
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                  {['Facility', 'Total Orders', 'Fulfilled', 'Pending', 'Fulfillment Rate'].map((h) => (
                    <th
                      key={h}
                      className="text-left py-2 px-3 font-600 text-[11px] uppercase tracking-wide"
                      style={{ color: '#94a3b8' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {TOP_CUSTOMERS.map((c, i) => {
                  const rate = Math.round((c.fulfilled / c.orders) * 100);
                  return (
                    <tr
                      key={c.name}
                      style={{ borderBottom: i < TOP_CUSTOMERS.length - 1 ? '1px solid #f8fafc' : 'none' }}
                    >
                      <td className="py-3 px-3 font-500" style={{ color: '#0f2744' }}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-700 text-white flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #0369a1, #0ea5e9)' }}
                          >
                            {c.name[0]}
                          </div>
                          {c.name}
                        </div>
                      </td>
                      <td className="py-3 px-3 font-600" style={{ color: '#0f2744' }}>{c.orders}</td>
                      <td className="py-3 px-3 font-500" style={{ color: '#16a34a' }}>{c.fulfilled}</td>
                      <td className="py-3 px-3 font-500" style={{ color: '#b45309' }}>{c.pending}</td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: '#f1f5f9', maxWidth: 80 }}>
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${rate}%`,
                                background: rate >= 90 ? '#16a34a' : rate >= 80 ? '#0369a1' : '#b45309',
                              }}
                            />
                          </div>
                          <span className="font-600 text-[11px]" style={{ color: '#0f2744' }}>{rate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </AppLayout>
  );
}
